import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import Swal from 'sweetalert2'
import * as authService from '../../services/authService'
import { pendingDepositDraftService, type PendingDepositDraft } from '../../services/pendingDepositDraftService'
import {
  cancelUserDepositTransaction,
  getTransactionSlipPreviewUrl,
  getUserTransactions,
  type WalletTransaction,
} from '../../services/walletTransactionService'
import * as walletService from '../../services/walletService'
import { HistoryCollapseCard, type HistoryCardLeftIcon } from './HistoryCollapseCard'
import { translateDepositReviewReason } from '../../utils/depositReviewReason'

type WalletHistoryMode = 'deposit' | 'withdraw' | 'credit'

type HistoryItem = {
  id: string
  refId: string
  title: string
  subtitle: string
  amountText: string
  amountClassName: string
  leftIcon: HistoryCardLeftIcon
  rows: Array<{ label: string; value: string }>
  searchText: string
  sortAt: number
  queue?: 'completed' | 'pending'
  actionLabel?: string
  onAction?: () => void
  secondaryActionLabel?: string
  onSecondaryAction?: () => void
  dangerActionLabel?: string
  onDangerAction?: () => void
}

const TYPE_LABEL: Record<string, string> = {
  deposit: 'ฝากเครดิต',
  withdraw: 'ถอนเครดิต',
  bonus: 'โบนัส',
  transfer_in: 'รับโอนเข้า',
  transfer_out: 'โอนออก',
  refund: 'คืนเงิน',
  adjustment: 'ปรับยอด',
  fee: 'ค่าธรรมเนียม',
  bet: 'เดิมพัน',
  win: 'รับรางวัล',
  cancel: 'ยกเลิกรายการ',
}

const STATUS_LABEL: Record<string, string> = {
  draft: 'รอทำรายการต่อ',
  pending: 'รอดำเนินการ',
  processing: 'กำลังดำเนินการ',
  reserved: 'รอโอนเงิน',
  verified: 'อนุมัติ',
  completed: 'สำเร็จ',
  approved: 'สำเร็จ',
  failed: 'ไม่สำเร็จ',
  rejected: 'ปฏิเสธ',
  cancelled: 'ยกเลิก',
  expired: 'หมดอายุ',
  locked: 'รอตรวจสอบเพิ่มเติม',
}

function formatDateTime(dateStr?: string | null): string {
  if (!dateStr) return '-'
  try {
    return new Date(dateStr).toLocaleString('th-TH', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  } catch {
    return dateStr
  }
}

function formatMoney(value?: number | string | null): string {
  return walletService.formatCurrency(Number(value) || 0)
}

function getStatusLabel(status?: string): string {
  return STATUS_LABEL[String(status || '').toLowerCase()] || String(status || '-')
}

function getTransactionTypeLabel(type?: string): string {
  return TYPE_LABEL[String(type || '').toLowerCase()] || String(type || '-')
}

function getLeftIconFromStatus(status?: string): HistoryCardLeftIcon {
  const normalized = String(status || '').toLowerCase()
  if (['verified', 'completed', 'approved'].includes(normalized)) return 'success'
  if (['rejected', 'failed', 'cancelled', 'expired'].includes(normalized)) return 'reject'
  if (['draft', 'pending', 'processing', 'reserved', 'locked'].includes(normalized)) return 'pending'
  return 'info'
}

function getSignedAmountText(amount: number): string {
  if (amount > 0) return `+${formatMoney(amount)} บาท`
  if (amount < 0) return `-${formatMoney(Math.abs(amount))} บาท`
  return `${formatMoney(0)} บาท`
}

function normalizeWithdrawUserText(value?: string | null): string {
  const normalized = String(value || '').trim()
  if (!normalized) return ''

  const genericValues = new Set([
    '-',
    'bank_transfer',
    'user_withdrawal_request',
    'User withdrawal request',
  ])

  return genericValues.has(normalized) ? '' : normalized
}

function getWithdrawChannelLabel(tx: WalletTransaction): string {
  const category = String(tx.category || '').trim().toLowerCase()
  if (category === 'bank_transfer') return 'โอนเข้าบัญชีธนาคาร'
  return category ? category : 'ถอนเข้าบัญชีธนาคาร'
}

function getWithdrawNote(tx: WalletTransaction): string {
  const status = String(tx.status || '').trim().toLowerCase()
  const approvalStatus = String(tx.approval?.status || '').trim().toLowerCase()
  const explicitReason =
    normalizeWithdrawUserText(tx.approval?.rejectionReason)
    || normalizeWithdrawUserText(tx.metadata?.notes)
    || normalizeWithdrawUserText(tx.details?.description)

  if (status === 'completed' || status === 'approved') {
    return explicitReason || 'ระบบโอนเงินเข้าบัญชีของคุณเรียบร้อยแล้ว'
  }

  if (status === 'reserved') {
    return explicitReason || 'เจ้าหน้าที่รับรายการแล้ว และกำลังเตรียมโอนเงินให้คุณ'
  }

  if (status === 'processing') {
    return explicitReason || (
      approvalStatus === 'approved'
        ? 'รายการถอนของคุณได้รับการอนุมัติแล้ว และกำลังดำเนินการโอนเงิน'
        : 'รายการถอนของคุณกำลังอยู่ระหว่างตรวจสอบ'
    )
  }

  if (status === 'locked') {
    return explicitReason || 'รายการถอนของคุณกำลังรอตรวจสอบเพิ่มเติมจากเจ้าหน้าที่'
  }

  if (status === 'expired') {
    return explicitReason || 'รายการถอนหมดอายุ กรุณาส่งคำขอถอนใหม่อีกครั้ง'
  }

  if (['failed', 'rejected', 'cancelled'].includes(status)) {
    return explicitReason || 'รายการถอนยังไม่สำเร็จ กรุณาติดต่อเจ้าหน้าที่หากยอดเงินยังไม่กลับเข้าระบบ'
  }

  if (approvalStatus === 'pending') {
    return explicitReason || 'รายการถอนของคุณถูกส่งแล้ว และกำลังรอเจ้าหน้าที่อนุมัติ'
  }

  if (approvalStatus === 'approved') {
    return explicitReason || 'รายการถอนของคุณได้รับการอนุมัติแล้ว และกำลังรอการโอนเงิน'
  }

  return explicitReason || 'รายการถอนของคุณถูกส่งแล้ว และกำลังอยู่ระหว่างดำเนินการ'
}

function getDepositTargetAccount(tx: WalletTransaction): {
  bankName: string
  accountName: string
  accountNumber: string
} {
  return {
    bankName: String(tx.metadata?.selectedDepositBankName || tx.details?.bankAccount?.bankCode || '').trim() || 'รายการฝากเงิน',
    accountName: String(tx.metadata?.selectedDepositAccountName || tx.details?.bankAccount?.accountName || '').trim() || '-',
    accountNumber: String(tx.metadata?.selectedDepositAccountNumber || tx.details?.bankAccount?.accountNumber || '').trim() || '-',
  }
}

function getDepositChannelLabel(tx: WalletTransaction): string {
  const method = String(tx.metadata?.depositMethod || '').trim().toLowerCase()
  if (method === 'prompt_pay') return 'พร้อมเพย์'
  if (method === 'true_money') return 'TrueMoney'
  if (method === 'decimal_reservation') return 'Payment Gateway'
  const sourceChannel = String(tx.metadata?.depositRouting?.sourceChannel || '').trim().toLowerCase()
  if (sourceChannel === 'payment') return 'Payment Gateway'
  if (sourceChannel === 'slip') return 'โอนผ่านธนาคารพร้อมสลิป'
  return 'โอนผ่านธนาคาร'
}

function getSlipStatusLabel(tx: WalletTransaction): string {
  if (tx.metadata?.submittedSlip?.imageUrl || tx.slipVerification?.slipData?.imageUrl) {
    const slipStatus = String(tx.slipVerification?.status || '').trim().toLowerCase()
    if (slipStatus === 'verified') return 'แนบแล้วและตรวจสอบผ่าน'
    if (slipStatus === 'rejected') return 'แนบแล้วแต่ตรวจสอบไม่ผ่าน'
    if (slipStatus === 'submitted' || slipStatus === 'pending') return 'แนบแล้ว รอตรวจสอบ'
    return 'แนบแล้ว'
  }
  return 'ยังไม่แนบสลิป'
}

function canAttachSlipToTransaction(tx: WalletTransaction): boolean {
  const status = String(tx.status || '').trim().toLowerCase()
  const hasSlip = Boolean(tx.metadata?.submittedSlip?.imageUrl || tx.slipVerification?.slipData?.imageUrl)
  const sourceChannel = String(tx.metadata?.depositRouting?.sourceChannel || '').trim().toLowerCase()
  const isPayment = sourceChannel === 'payment' || Boolean(tx.metadata?.paymentGateway?.provider)
  return ['pending', 'processing', 'reserved', 'locked'].includes(status) && !hasSlip && !isPayment
}

function canReattachSlipToTransaction(tx: WalletTransaction): boolean {
  const status = String(tx.status || '').trim().toLowerCase()
  return ['pending', 'processing', 'reserved', 'locked'].includes(status)
}

function normalizeSearchValue(value?: string | number | null): string {
  return String(value ?? '').trim().toLowerCase()
}

function buildSearchBlob(parts: Array<string | number | null | undefined>): string {
  return parts
    .map((part) => normalizeSearchValue(part))
    .filter(Boolean)
    .join(' ')
}

function buildDepositDraftItem(draft: PendingDepositDraft, onContinue?: () => void): HistoryItem {
  const refId = draft.id
  const amount = Number(draft.amount) || 0
  const updatedAt = draft.updatedAt || draft.createdAt
  return {
    id: `draft:${draft.id}`,
    refId,
    title: draft.depositAccount?.bankName || 'รายการฝากค้าง',
    subtitle: formatDateTime(updatedAt),
    amountText: `${formatMoney(amount)} บาท`,
    amountClassName: 'c_tertiary font_md',
    leftIcon: 'pending',
    sortAt: Date.parse(updatedAt || '') || 0,
    queue: 'pending',
    actionLabel: onContinue ? 'ทำรายการต่อ' : undefined,
    onAction: onContinue,
    rows: [
      { label: 'สถานะ', value: getStatusLabel('draft') },
      { label: 'วิธีฝาก', value: draft.depositMethod },
      { label: 'จำนวนเงิน', value: `${formatMoney(amount)} บาท` },
      { label: 'บัญชีฝาก', value: draft.depositAccount?.accountNumber || '-' },
      { label: 'ชื่อบัญชี', value: draft.depositAccount?.accountName || '-' },
      { label: 'อัปเดตล่าสุด', value: formatDateTime(updatedAt) },
    ],
    searchText: buildSearchBlob([
      refId,
      draft.depositMethod,
      draft.depositAccount?.bankName,
      draft.depositAccount?.accountName,
      draft.depositAccount?.accountNumber,
      getStatusLabel('draft'),
      'draft',
      'pending',
      'รอทำรายการต่อ',
      'รอดำเนินการ',
      amount,
      formatMoney(amount),
    ]),
  }
}

function buildDepositTransactionItem(
  tx: WalletTransaction,
  onAttachSlip?: () => void,
  onViewSlip?: () => void,
  onCancel?: () => void
): HistoryItem | null {
  const status = String(tx.status || '').trim().toLowerCase()
  const queue = status === 'completed'
    ? 'completed'
    : ['pending', 'processing', 'reserved', 'locked'].includes(status)
      ? 'pending'
      : null

  if (!queue) return null

  const refId = tx.transactionId || tx._id
  const createdAt = tx.audit?.createdAt || tx.createdAt || tx.audit?.updatedAt || tx.updatedAt || ''
  const amount = Number(tx.amount) || 0
  const account = getDepositTargetAccount(tx)
  const hasSlip = Boolean(tx.metadata?.submittedSlip?.imageUrl || tx.slipVerification?.slipData?.imageUrl)
  const canAttachSlip = canAttachSlipToTransaction(tx)
  const canReattachSlip = canReattachSlipToTransaction(tx)
  const autoDecision = String(tx.metadata?.depositRouting?.autoCreditDecision || '').trim().toLowerCase()
  const canCancel = queue === 'pending' && autoDecision !== 'await_callback' && Boolean(onCancel)

  return {
    id: `deposit:${tx._id}`,
    refId,
    title: account.bankName,
    subtitle: formatDateTime(createdAt),
    amountText: `${formatMoney(amount)} บาท`,
    amountClassName: queue === 'completed' ? 'c_success font_md' : 'c_tertiary font_md',
    leftIcon: queue === 'completed' ? 'success' : getLeftIconFromStatus(tx.status),
    sortAt: Date.parse(createdAt || '') || 0,
    queue,
    actionLabel: hasSlip && onViewSlip ? 'ดูสลิป' : canAttachSlip && onAttachSlip ? 'แนบสลิปภายหลัง' : undefined,
    onAction: hasSlip && onViewSlip ? onViewSlip : canAttachSlip && onAttachSlip ? onAttachSlip : undefined,
    secondaryActionLabel: hasSlip && canReattachSlip && onAttachSlip ? 'แนบใหม่' : undefined,
    onSecondaryAction: hasSlip && canReattachSlip && onAttachSlip ? onAttachSlip : undefined,
    dangerActionLabel: canCancel ? 'ยกเลิก' : undefined,
    onDangerAction: canCancel ? onCancel : undefined,
    rows: [
      { label: 'สถานะ', value: getStatusLabel(tx.status) },
      { label: 'ช่องทางฝาก', value: getDepositChannelLabel(tx) },
      { label: 'จำนวนเงิน', value: `${formatMoney(amount)} บาท` },
      { label: 'บัญชีปลายทาง', value: account.accountNumber },
      { label: 'ชื่อบัญชี', value: account.accountName },
      { label: 'สลิป', value: hasSlip ? `${getSlipStatusLabel(tx)} (กดดูสลิปได้)` : getSlipStatusLabel(tx) },
      {
        label: 'หมายเหตุ',
        value: translateDepositReviewReason(
          tx.metadata?.depositRouting?.reviewReason || tx.metadata?.notes || tx.details?.description || null,
          '-'
        ),
      },
      { label: 'เวลาทำรายการ', value: formatDateTime(createdAt) },
    ],
    searchText: buildSearchBlob([
      refId,
      account.bankName,
      account.accountName,
      account.accountNumber,
      tx.status,
      getStatusLabel(tx.status),
      tx.metadata?.notes,
      tx.metadata?.depositRouting?.reviewReason,
      getDepositChannelLabel(tx),
      amount,
      formatMoney(amount),
      queue === 'completed' ? 'สำเร็จ สำเร็จแล้ว completed approved verified' : 'รอดำเนินการ pending processing reserved locked',
    ]),
  }
}

function isCreditTransaction(tx: WalletTransaction): boolean {
  const type = String(tx.type || '').toLowerCase()
  const amount = Number(tx.amount) || 0
  if (amount <= 0) return false
  return ['bonus', 'refund', 'adjustment', 'transfer_in', 'win', 'cancel'].includes(type)
}

function buildWalletTransactionItem(tx: WalletTransaction, mode: WalletHistoryMode): HistoryItem {
  const refId = tx.transactionId || tx._id
  const createdAt = tx.audit?.createdAt || tx.createdAt || tx.audit?.updatedAt || tx.updatedAt || ''
  const amount = Number(tx.amount) || 0
  const isPositive = amount >= 0
  const isWithdrawMode = mode === 'withdraw'
  const withdrawBankAccount = tx.details?.bankAccount?.accountNumber || '-'
  const withdrawBankAccountName = tx.details?.bankAccount?.accountName || '-'
  return {
    id: `${mode}:${tx._id}`,
    refId,
    title:
      isWithdrawMode
        ? tx.details?.bankAccount?.bankCode || 'ถอนเงิน'
        : getTransactionTypeLabel(tx.type),
    subtitle: formatDateTime(createdAt),
    amountText: getSignedAmountText(amount),
    amountClassName: `${isPositive ? 'c_success' : 'c_primary'} font_md`,
    leftIcon:
      mode === 'credit' && isPositive
        ? 'success'
        : getLeftIconFromStatus(tx.status),
    sortAt: Date.parse(createdAt || '') || 0,
    rows: isWithdrawMode
      ? [
          { label: 'สถานะ', value: getStatusLabel(tx.status) },
          { label: 'ประเภทรายการ', value: getTransactionTypeLabel(tx.type) },
          { label: 'จำนวนเงิน', value: getSignedAmountText(amount) },
          { label: 'ช่องทางถอน', value: getWithdrawChannelLabel(tx) },
          { label: 'เลขบัญชีรับเงิน', value: withdrawBankAccount },
          { label: 'ชื่อบัญชีรับเงิน', value: withdrawBankAccountName },
          { label: 'หมายเหตุ', value: getWithdrawNote(tx) },
          { label: 'เวลาทำรายการ', value: formatDateTime(createdAt) },
        ]
      : [
          { label: 'สถานะ', value: getStatusLabel(tx.status) },
          { label: 'ประเภทรายการ', value: getTransactionTypeLabel(tx.type) },
          { label: 'จำนวนเงิน', value: getSignedAmountText(amount) },
          { label: 'หมวดหมู่', value: tx.category || '-' },
          { label: 'บัญชีธนาคาร', value: tx.details?.bankAccount?.accountNumber || '-' },
          { label: 'ชื่อบัญชี', value: tx.details?.bankAccount?.accountName || '-' },
          { label: 'อ้างอิง', value: tx.details?.reference || refId },
          { label: 'หมายเหตุ', value: translateDepositReviewReason(tx.details?.description || null, '-') },
          { label: 'เวลาทำรายการ', value: formatDateTime(createdAt) },
        ],
    searchText: buildSearchBlob([
      refId,
      tx.details?.reference,
      tx.details?.description,
      tx.details?.bankAccount?.accountNumber,
      tx.details?.bankAccount?.accountName,
      tx.details?.bankAccount?.bankCode,
      tx.status,
      getStatusLabel(tx.status),
      tx.type,
      getTransactionTypeLabel(tx.type),
      tx.category,
      amount,
      formatMoney(amount),
      getSignedAmountText(amount),
      isWithdrawMode ? getWithdrawChannelLabel(tx) : '',
      isWithdrawMode ? getWithdrawNote(tx) : '',
    ]),
  }
}

function getPageTitle(mode: WalletHistoryMode): string {
  if (mode === 'deposit') return 'ประวัติการฝากเงิน'
  if (mode === 'withdraw') return 'ประวัติการถอนเงิน'
  return 'ประวัติการรับเครดิต'
}

function getEmptyMessage(mode: WalletHistoryMode, queue?: 'completed' | 'pending'): string {
  if (mode === 'deposit') {
    return queue === 'pending' ? 'ยังไม่มีรายการฝากที่รอดำเนินการ' : 'ยังไม่มีประวัติการฝากเงินที่สำเร็จ'
  }
  if (mode === 'withdraw') return 'ยังไม่มีประวัติการถอนเงิน'
  return 'ยังไม่มีประวัติการรับเครดิต'
}

interface HistoryWalletReportProps {
  mode: WalletHistoryMode
}

type DepositHistoryLocationState = {
  depositJustSubmitted?: boolean
  depositSuccessMessage?: string
  depositPreferredQueue?: 'completed' | 'pending'
}

const HistoryWalletReport: React.FC<HistoryWalletReportProps> = ({ mode }) => {
  const navigate = useNavigate()
  const location = useLocation()
  const [items, setItems] = useState<HistoryItem[]>([])
  const [searchQuery, setSearchQuery] = useState('')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [depositQueue, setDepositQueue] = useState<'completed' | 'pending'>('pending')
  const [depositSuccessMessage, setDepositSuccessMessage] = useState<string | null>(null)

  const handlePreviewTransactionSlip = useCallback(async (transactionId: string) => {
    let previewUrl = ''
    try {
      previewUrl = await getTransactionSlipPreviewUrl(transactionId)
      await Swal.fire({
        title: 'ดูสลิปการโอน',
        imageUrl: previewUrl,
        imageAlt: 'slip payment',
        width: 720,
        confirmButtonText: 'ปิด',
        customClass: {
          popup: 'rounded-4',
        },
      })
    } catch (err) {
      await Swal.fire({
        icon: 'error',
        title: 'ดูสลิปไม่สำเร็จ',
        text: err instanceof Error ? err.message : 'ไม่สามารถโหลดรูปสลิปได้',
        confirmButtonText: 'ปิด',
      })
    } finally {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl)
      }
    }
  }, [])

  const handleCancelDeposit = useCallback(async (transactionId: string) => {
    const confirm = await Swal.fire({
      icon: 'warning',
      title: 'ยกเลิกรายการฝาก?',
      text: 'รายการจะหายจากแท็บรอดำเนินการ',
      showCancelButton: true,
      confirmButtonText: 'ยืนยันยกเลิก',
      cancelButtonText: 'ปิด',
      confirmButtonColor: '#dc2626',
    })
    if (!confirm.isConfirmed) return

    const result = await cancelUserDepositTransaction(transactionId)
    if (!result.success) {
      await Swal.fire({
        icon: 'error',
        title: 'ยกเลิกไม่สำเร็จ',
        text: result.message || 'ไม่สามารถยกเลิกรายการฝากได้',
        confirmButtonText: 'ปิด',
      })
      return
    }

    setItems((prev) => prev.filter((item) => item.id !== `deposit:${transactionId}`))
    await Swal.fire({
      icon: 'success',
      title: 'ยกเลิกแล้ว',
      text: 'เคลียร์รายการออกจากรอดำเนินการเรียบร้อย',
      timer: 1600,
      showConfirmButton: false,
    })
  }, [])

  const loadHistory = useCallback(async () => {
    const user = authService.getCurrentUser() as { id?: string; _id?: string; tenantId?: string } | null
    const userId = user?.id || user?._id || ''
    const tenantId = user?.tenantId || ''

    if (!userId || !tenantId) {
      setItems([])
      setError('กรุณาเข้าสู่ระบบ')
      setLoading(false)
      return
    }

    try {
      setLoading(true)
      setError(null)

      if (mode === 'deposit') {
        const drafts = pendingDepositDraftService.list(tenantId, userId)
        const transactionResponse = await getUserTransactions({
          type: 'deposit',
          limit: 100,
        })

        if (!transactionResponse.success) {
          throw new Error(transactionResponse.message || 'โหลดข้อมูลไม่สำเร็จ')
        }

        const depositTransactions = (transactionResponse.data?.transactions || [])
          .map((transaction) => buildDepositTransactionItem(
            transaction,
            () => navigate(`/topup/slip?transaction=${encodeURIComponent(transaction._id)}`),
            () => void handlePreviewTransactionSlip(transaction._id),
            () => void handleCancelDeposit(transaction._id)
          ))
          .filter((item): item is HistoryItem => Boolean(item))

        const nextItems = [
          ...depositTransactions,
          ...drafts.map((draft) => buildDepositDraftItem(
            draft,
            () => navigate(
              draft.depositAccount
                ? `/topup/slip?draft=${encodeURIComponent(draft.id)}`
                : `/topup/confirm?draft=${encodeURIComponent(draft.id)}`
            )
          )),
        ].sort((a, b) => b.sortAt - a.sortAt)
        const hasPendingItems = nextItems.some((item) => item.queue === 'pending')
        const hasCompletedItems = nextItems.some((item) => item.queue === 'completed')
        setDepositQueue(hasPendingItems ? 'pending' : hasCompletedItems ? 'completed' : 'pending')
        setItems(nextItems)
        return
      }

      const transactionResponse = await getUserTransactions({
        ...(mode === 'withdraw' ? { type: 'withdraw' as const } : {}),
        limit: 100,
      })

      if (!transactionResponse.success) {
        throw new Error(transactionResponse.message || 'โหลดข้อมูลไม่สำเร็จ')
      }

      const transactions = transactionResponse.data?.transactions || []
      const filtered = mode === 'credit'
        ? transactions.filter(isCreditTransaction)
        : transactions.filter((transaction) => transaction.type === 'withdraw')

      setItems(
        filtered
          .map((transaction) => buildWalletTransactionItem(transaction, mode))
          .sort((a, b) => b.sortAt - a.sortAt)
      )
    } catch (err) {
      setItems([])
      setError(err instanceof Error ? err.message : 'โหลดข้อมูลไม่สำเร็จ')
    } finally {
      setLoading(false)
    }
  }, [handleCancelDeposit, handlePreviewTransactionSlip, mode, navigate])

  useEffect(() => {
    loadHistory()
  }, [loadHistory])

  useEffect(() => {
    if (mode !== 'deposit') return
    const state = location.state as DepositHistoryLocationState | null
    if (!state?.depositJustSubmitted) return

    setDepositSuccessMessage(
      state.depositSuccessMessage?.trim() || 'ส่งรายการฝากเรียบร้อยแล้ว'
    )
    if (state.depositPreferredQueue === 'completed' || state.depositPreferredQueue === 'pending') {
      setDepositQueue(state.depositPreferredQueue)
    }

    navigate(location.pathname, { replace: true, state: null })
  }, [location.pathname, location.state, mode, navigate])

  const filteredItems = useMemo(() => {
    const query = searchQuery.trim().toLowerCase()
    // ตอนค้นหา: หาครบทั้งสำเร็จ/รอดำเนินการ ไม่จำกัดเฉพาะแท็บคิวปัจจุบัน
    const queueFiltered = mode === 'deposit' && !query
      ? items.filter((item) => item.queue === depositQueue)
      : items
    if (!query) return queueFiltered
    return queueFiltered.filter((item) => item.searchText.includes(query))
  }, [depositQueue, items, mode, searchQuery])

  const searchHint =
    mode === 'deposit'
      ? 'ค้นหาได้จากเลขอ้างอิง, ธนาคาร, เลขบัญชี, จำนวนเงิน, สถานะ (เช่น สำเร็จ / รอดำเนินการ)'
      : mode === 'withdraw'
        ? 'ค้นหาได้จากเลขอ้างอิง, เลขบัญชี, ชื่อบัญชี, จำนวนเงิน, สถานะ'
        : 'ค้นหาได้จากเลขอ้างอิง, ประเภทรายการ, จำนวนเงิน, สถานะ'

  return (
    <div className="htd-hist__stack">
      <h2 className="htd-hist__page-title">{getPageTitle(mode)}</h2>

      {mode === 'deposit' && depositSuccessMessage ? (
        <div className="htd-hist__success" role="status">
          <span className="htd-hist__success-icon" aria-hidden>
            <i className="fas fa-check-circle" />
          </span>
          <div className="htd-hist__success-copy">
            <strong>ส่งรายการฝากแล้ว</strong>
            <p>{depositSuccessMessage}</p>
          </div>
          <button
            type="button"
            className="htd-hist__success-close"
            onClick={() => setDepositSuccessMessage(null)}
            aria-label="ปิดข้อความ"
          >
            <i className="fas fa-times" />
          </button>
        </div>
      ) : null}

      <div className="htd-hist__panel">
        {mode === 'deposit' && (
          <div className="htd-hist__chips">
            <button
              type="button"
              className={`htd-hist__chip${depositQueue === 'completed' && !searchQuery.trim() ? ' is-active is-active--green' : ''}`}
              onClick={() => setDepositQueue('completed')}
              disabled={Boolean(searchQuery.trim())}
            >
              สำเร็จแล้ว
            </button>
            <button
              type="button"
              className={`htd-hist__chip${depositQueue === 'pending' && !searchQuery.trim() ? ' is-active' : ''}`}
              onClick={() => setDepositQueue('pending')}
              disabled={Boolean(searchQuery.trim())}
            >
              รอดำเนินการ
            </button>
          </div>
        )}
        <div className="htd-hist__search">
          <span className="htd-hist__search-icon" aria-hidden>
            <i className="fas fa-search" />
          </span>
          <input
            type="text"
            placeholder={
              mode === 'deposit'
                ? 'พิมพ์เลขอ้างอิง / ธนาคาร / เลขบัญชี / จำนวนเงิน...'
                : 'พิมพ์เลขอ้างอิง / บัญชี / จำนวนเงิน / สถานะ...'
            }
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {searchQuery.trim() && (
            <button
              type="button"
              className="htd-hist__search-clear"
              onClick={() => setSearchQuery('')}
              aria-label="ล้างคำค้นหา"
            >
              <i className="fas fa-times" />
            </button>
          )}
        </div>
        <span className="htd-hist__hint">{searchHint}</span>
        {searchQuery.trim() ? (
          <span className="htd-hist__hint">
            ผลการค้นหา: {filteredItems.length} รายการ
            {mode === 'deposit' ? ' (ค้นหาทั้งสำเร็จและรอดำเนินการ)' : ''}
          </span>
        ) : null}
      </div>

      {loading ? (
        <div className="htd-hist__loading">
          <h4>กำลังโหลดข้อมูล...</h4>
        </div>
      ) : error ? (
        <div className="htd-hist__shell">
          <h4>{error}</h4>
        </div>
      ) : filteredItems.length === 0 ? (
        <div className="htd-hist__empty">
          <h4>
            {searchQuery.trim()
              ? `ไม่พบรายการที่ตรงกับ “${searchQuery.trim()}”`
              : getEmptyMessage(mode, depositQueue)}
          </h4>
          {searchQuery.trim() ? (
            <p>ลองค้นด้วยเลขอ้างอิงบางส่วน เลขบัญชี หรือจำนวนเงิน</p>
          ) : null}
        </div>
      ) : (
        <div className="htd-hist__list">
          {filteredItems.map((item) => (
            <HistoryCollapseCard
              key={item.id}
              id={item.id}
              leftIcon={item.leftIcon}
              refId={item.refId}
              rows={item.rows}
              onCopyRef={() => navigator.clipboard.writeText(item.refId).catch(() => undefined)}
              actionLabel={item.actionLabel}
              onAction={item.onAction}
              secondaryActionLabel={item.secondaryActionLabel}
              onSecondaryAction={item.onSecondaryAction}
              dangerActionLabel={item.dangerActionLabel}
              onDangerAction={item.onDangerAction}
              header={
                <>
                  <h5>{item.title}</h5>
                  <h5>{item.subtitle}</h5>
                  <h5 className={item.amountClassName}>{item.amountText}</h5>
                </>
              }
            />
          ))}
        </div>
      )}
    </div>
  )
}

export default HistoryWalletReport
